<template>
  <div v-if="!isReadonly" class="asset-drawer__form">
    <UIDocumentDropdown
      :disabled="isButtonDisabled"
      label="Document"
      placement="bottom-start"
      variant="secondary"
      size="sm"
      full
      @select="onSelectService"
    />
  </div>
  <UIGridSkeleton
    v-if="isLoading"
    v-bind="{ columns, sm }"
    message="Loading documents..."
  />
  <UIGrid
    v-else
    v-model:sort="sort"
    data-refid="assetDocumentsList"
    v-bind="{ columns, items, sm }"
  >
    <template v-if="!isReadonly" #actions="{ item, size }">
      <UIButton
        v-if="item"
        label="Edit"
        :leading="PencilIcon"
        fill="light"
        :size
        @click="handleClickEdit(item)"
      />
      <UIButton
        v-if="item"
        label="Unlink"
        :leading="TrashIcon"
        variant="danger"
        fill="light"
        :size
        @click="handleClickUnlink(item)"
      />
      <UIDocumentLink :document-id="item.id" :name="item.name" :path="item.path" :source="item.source" />
    </template>
    <template #cellPath="{ item }">
      <DocumentPath :item="documentsBunchStore.getElementById(item.id)" />
    </template>
    <template #cellSource="{ item }">
      <DocumentsSource :item="documentsBunchStore.getElementById(item.id)" />
    </template>
    <template #cellTags="{ displayValue }">
      <UITagsList
        v-if="displayValue.length"
        :model-value="displayValue"
        readonly
      />
      <span v-else>&nbsp;</span>
    </template>
  </UIGrid>
</template>

<script setup lang="ts">
import { computed, inject, markRaw, ref } from 'vue'

import { AssetClass } from '..'
import { DocumentSource, ModalEvent, ReadonlyMode, Sort } from '@types'
import { Document } from '@/entities/documents/utils/types'
import { EntityEvent } from '@/entities/utils/enums'

import { READONLY_MODE } from '@/const/common'

import { getDocumentTagName } from '@/entities/assets/components/utils/helpers'
import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'
import { useDocumentService } from '@/plugins/services'

import useGridSort from '@/components/hooks/gridSort'

import { useDocumentsBunchStore } from '@/store/documents/bunch'
import { useDocumentsStore } from '@/store/documents/index'
import { useModalsStore } from '@/store/modals'

import {
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import { UIDocumentDropdown, UITagsList, UIDocumentLink } from '@ui/core'
import { UIButton } from '@ui/buttons'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import { UIDialogRemove } from '@ui/modals'

import DocumentPath from '@/views/Data/Documents/DocumentPath.vue'
import DocumentsSource from '@/views/Data/Documents/DocumentsSource.vue'

type Props = {
  instance: AssetClass
}

const props = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const documentsBunchStore = useDocumentsBunchStore()
const documentsStore = useDocumentsStore()
const modalsStore = useModalsStore()

const { error } = useNotifications()

const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const loading = ref(false)

const sort = ref<Sort[]>()

const removedDocument = ref<Document>()

const isLoading = computed(
  () =>
    loading.value || !documentsStore.initFlag || documentsStore.loadingAction,
)

const isButtonDisabled = computed(() => isLoading.value)

const assetId = computed(() => props.instance.id)

const sm = '1fr'
const columns = computed(() => [
  {
    name: 'name',
    caption: 'Document',
    hideCaptionIfCollapsed: true,
  },
  {
    name: 'path',
    caption: 'Path',
    hideCaptionIfCollapsed: true,
  },
  {
    name: 'source',
    caption: 'Source',
    hideCaptionIfCollapsed: true,
  },
  {
    name: 'tags',
    caption: '',
    unsortable: true,
    unresizeable: true,
    tooltip: null,
    formatter: (value?: string[]) =>
      value?.map(id => ({ id, name: getDocumentTagName(id) })),
    hideCaptionIfCollapsed: true,
  },
])

const inputItems = computed(
  () =>
  props.instance.docs
    .map(id => {
      const instance = documentsBunchStore.getElementById(id)
      return instance?.get()
    })
    .filter(item => item !== undefined) as Document[],
)
const items = useGridSort(sort, columns, inputItems)

const handleClickEdit = (value: Document) => {
  const instance = documentsBunchStore.getElementById(value.id)
  if (!instance) return
  instance.isDialog = true
  const removeDialogFlag = () => {
    instance.isDialog = false
  }
  instance?.addEventListener(EntityEvent.STORED, removeDialogFlag)
  const key = `asset-drawer-document-${instance.id}`
  const modalInstance = modalsStore.init(key, instance)
  modalInstance?.open(modalsStore.getZIndex())
  modalInstance?.addEventListener(ModalEvent.CLOSE, removeDialogFlag)
}

const handleClickUnlink = (item: Document) => {
  removedDocument.value = item
  const key = `asset-remove-document-${item.id}`
  const modalInstance = modalsStore.init(key, markRaw(UIDialogRemove))
  modalInstance?.open(modalsStore.getZIndex(), {
    title: `Unlink asset document '${removedDocument.value?.name}'`,
    message: 'Are you sure you want to unlink this document?',
    label: 'Unlink',
    onRemove: handleRemove,
  })
}

const handleRemove = async () => {
  const id = removedDocument.value?.id
  removedDocument.value = undefined
  if (!id) return
  loading.value = true
  try {
    const document = documentsBunchStore.getElementById(id)
    document?.set({
      asset_id: null,
    })
    document?.update()
    // eslint-disable-next-line vue/no-mutating-props -- AssetClass instance is a mutable active-record by design
    props.instance.docs = props.instance.docs.filter(item => item !== id)
  } catch (e) {
    await error({
      message: 'There was a problem with the document',
    })
    handleCatchedError(e as string)
  } finally {
    loading.value = false
  }
}

const handleCreate = (name: string, path: string, source: DocumentSource) => {
  const instance = documentsBunchStore.createElement()
  instance.set({
    name,
    path,
    source,
    asset_id: assetId.value,
  })
  instance.isDialog = true
  instance.addEventListener(EntityEvent.STORED, () => {
    instance.isDialog = false
  })
  const key = `asset-create-document-${instance.id}`
  const modalInstance = modalsStore.init(key, instance)
  modalInstance?.open(modalsStore.getZIndex(), { size: 'sm' })
  modalInstance?.addEventListener(ModalEvent.CLOSE, () => {
    if (!instance.isNew) return
    instance.remove()
  })
}

const { onSelectService } = useDocumentService(handleCreate)
</script>
